import apiFetch from '@scale/frontend-lib/api-fetch';
import { useAsync } from 'react-use-v13';
import { INamedObjectId, INamedObjectTypes, IObjectQuery } from '@scale/shared/types/lookup';
import { isScaleBot } from '@scale/shared/defaults';

/**
 * This object lets us request metadata about individual object IDs.
 * If multiple requests are made in a short period of time, they
 * will be "bundled" as a single network request. However, this is opaque
 * to callers: You can simply `await bundler.fetch(id, type)` and the
 * call will resolve when the data becomes available in the backend.
 * Also, results are cached - even when the same request is made before the previous
 * result has returned.
 */
class NameObjectIdBundler {
  _buffer: IObjectQuery[] = [];
  _dataMap: Record<string, Promise<INamedObjectId>> = {};
  _resolvers: Record<string, [(obj: INamedObjectId) => void, (err: any) => void]> = {};
  _debounceTime: number;
  _maxBatchSize: number;
  _lastUpdate = 0;
  _timer: ReturnType<typeof setTimeout> | null = null;
  constructor({
    debounceTime,
    maxBatchSize,
  }: { debounceTime?: number; maxBatchSize?: number } = {}) {
    this._debounceTime = debounceTime || 500;
    this._maxBatchSize = maxBatchSize || 50;
  }

  private async _batchedDebouncedFetch() {
    if (this._timer) {
      clearTimeout(this._timer);
    }
    const now = new Date().getTime();
    if (this._lastUpdate === 0) this._lastUpdate = now - this._debounceTime + 50; // Allow slight buffer to allow collecting first batch
    const targetUpdateTime = this._lastUpdate + this._debounceTime;
    if (targetUpdateTime > now) {
      // Fetch later
      this._timer = setTimeout(() => this._batchedDebouncedFetch(), targetUpdateTime - now);
      return;
    }
    // Fetch now
    this._lastUpdate = now;
    const batch = this._buffer.splice(0, this._maxBatchSize);
    if (!batch.length) return;
    const { objects } = await apiFetch<{ objects: Record<string, INamedObjectId | null> }>(
      null,
      `corp-api/lookup/batch`,
      {
        method: 'POST',
        body: { batch },
      },
    );

    // Resolve promises
    for (const [id, object] of Object.entries(objects)) {
      if (!this._resolvers[id]) continue;
      const [resolve, reject] = this._resolvers[id];
      if (!object) reject(object);
      else resolve(object);
    }

    // If there still objects to lookup, fetch now...
    this._batchedDebouncedFetch();
  }

  async fetch(id: string, type: INamedObjectTypes): Promise<INamedObjectId> {
    // Special cases
    if (!id) {
      return {
        id,
        type,
        name: id,
      };
    }

    if (isScaleBot(id)) {
      return {
        id,
        type,
        name: 'Scale Bot',
      };
    }

    // Is the result cached?
    if (id in this._dataMap) return this._dataMap[id];

    // Schedule lookup
    this._buffer.push({ id, type });
    this._dataMap[id] = new Promise((resolve, reject) => {
      this._resolvers[id] = [resolve, reject];
    });
    this._batchedDebouncedFetch();
    return this._dataMap[id];
  }
}

const bundler = new NameObjectIdBundler();

export const useNameObjectId = (id: string, type: INamedObjectTypes) => {
  return useAsync(() => bundler.fetch(id, type), [id]);
};
